'use client'

import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import './seo-intelligence-case-study.css'

/**
 * SEO Intelligence Platform case study.
 *
 * A port of `public/radar-branding/seo-intelligence-new.html`. That file is the
 * design contract: markup, class names, copy and section order match it, so the
 * two can be diffed. Styling lives in the sibling
 * `seo-intelligence-case-study.css`, scoped under `.sip-cs`.
 *
 * Renders full-bleed -- `WorkPage`'s hero and container are skipped for this
 * slug (see `fullBleedProjects` in ./index.ts) because it ships its own hero.
 *
 * The source drove the dashboard with an IIFE writing innerHTML on every
 * interaction. Here the filtering and arithmetic are pure and everything is
 * rendered from state. The numbers are unchanged.
 *
 * Every figure is synthetic. The source says so throughout ("mock", "synthetic
 * GSC", "Not a live Search Console connection") and that copy is carried over
 * verbatim -- it is what keeps the demo honest.
 *
 * Its own header, footer, contact strip and closing CTA are dropped: the site
 * chrome renders all four, and the last two would duplicate the layout's bands.
 *
 * The "See it in action" band and the hero's second CTA are also gone: both
 * pointed at /seo-intelligence, the hosted demo, which was removed on
 * 2026-08-13. The in-page mock dashboard above is now the only demo.
 */

const PLATFORMS = [
  { id: 'chatgpt', name: 'ChatGPT' },
  { id: 'perplexity', name: 'Perplexity' },
  { id: 'claude', name: 'Claude' },
  { id: 'gemini', name: 'Gemini' },
  { id: 'aio', name: 'AI Overviews' },
] as const

interface Query {
  id: string
  query: string
  intent: 'Commercial' | 'Informational' | 'Comparison'
  page: string
  impressions: number
  clicks: number
  position: number
  citations: string[]
  brief: string
}

/** Synthetic query set -- not Search Console data. */
const QUERIES: Query[] = [
  {
    id: 'visibility-audit',
    query: 'ai visibility audit',
    intent: 'Commercial',
    page: '/services/ai-visibility-strategy',
    impressions: 18400,
    clicks: 812,
    position: 4.2,
    citations: ['chatgpt', 'perplexity', 'gemini', 'aio'],
    brief: 'AI Visibility Audit: What It Measures and How to Act',
  },
  {
    id: 'geo',
    query: 'generative engine optimization',
    intent: 'Informational',
    page: '/blogs/generative-engine-optimization',
    impressions: 12100,
    clicks: 418,
    position: 8.4,
    citations: ['chatgpt', 'perplexity', 'claude'],
    brief: 'Generative Engine Optimization: A Practical Operating Guide',
  },
  {
    id: 'product-agency',
    query: 'ai product development agency',
    intent: 'Commercial',
    page: '/services/ai-product-development',
    impressions: 12800,
    clicks: 412,
    position: 5.8,
    citations: ['chatgpt', 'perplexity', 'gemini'],
    brief: 'How to Evaluate an AI Product Development Agency',
  },
  {
    id: 'aeo-services',
    query: 'answer engine optimization services',
    intent: 'Commercial',
    page: '/services/ai-visibility-strategy',
    impressions: 7600,
    clicks: 213,
    position: 10.6,
    citations: ['chatgpt', 'claude'],
    brief: 'Answer Engine Optimization Services: Scope, Evidence, and Fit',
  },
  {
    id: 'geo-ph',
    query: 'geo agency philippines',
    intent: 'Commercial',
    page: '/services/ai-visibility-strategy',
    impressions: 5900,
    clicks: 219,
    position: 6.7,
    citations: ['perplexity'],
    brief: 'Choosing a GEO Agency in the Philippines',
  },
  {
    id: 'llms',
    query: 'what is llms.txt',
    intent: 'Informational',
    page: '/blogs/what-is-llms-txt',
    impressions: 11200,
    clicks: 680,
    position: 3.1,
    citations: ['chatgpt', 'perplexity', 'claude', 'gemini', 'aio'],
    brief: 'What Is llms.txt? Format, Limits, and Implementation',
  },
  {
    id: 'sales-qualification',
    query: 'ai sales qualification',
    intent: 'Comparison',
    page: '/vector',
    impressions: 4800,
    clicks: 188,
    position: 7.1,
    citations: ['chatgpt', 'claude', 'gemini', 'aio'],
    brief: 'AI Sales Qualification: Models, Guardrails, and Handoffs',
  },
  {
    id: 'multi-agent',
    query: 'multi agent ai systems',
    intent: 'Informational',
    page: '/hive',
    impressions: 7600,
    clicks: 241,
    position: 9.2,
    citations: ['chatgpt', 'perplexity', 'claude'],
    brief: 'Multi-Agent AI Systems: When Shared Context Matters',
  },
  {
    id: 'ai-search',
    query: 'ai search visibility',
    intent: 'Informational',
    page: '/platform',
    impressions: 13400,
    clicks: 512,
    position: 5.1,
    citations: ['chatgpt', 'perplexity', 'gemini', 'aio'],
    brief: 'AI Search Visibility: The Metrics That Actually Matter',
  },
  {
    id: 'seo-platform',
    query: 'seo intelligence platform',
    intent: 'Comparison',
    page: '/projects/seo-intelligence-platform',
    impressions: 3800,
    clicks: 174,
    position: 2.9,
    citations: ['chatgpt', 'perplexity'],
    brief: 'SEO Intelligence Platforms: Search and Citation Signals',
  },
  {
    id: 'citation-tracker',
    query: 'ai citation tracker',
    intent: 'Commercial',
    page: '/tools/ai-citation-tracker',
    impressions: 9600,
    clicks: 390,
    position: 4.8,
    citations: ['chatgpt', 'perplexity', 'claude', 'gemini'],
    brief: 'AI Citation Tracker: Monitor, Verify, and Improve Mentions',
  },
  {
    id: 'invisible',
    query: 'website invisible to chatgpt',
    intent: 'Informational',
    page: '/blogs/website-invisible-to-chatgpt',
    impressions: 6400,
    clicks: 247,
    position: 7.8,
    citations: ['chatgpt', 'gemini'],
    brief: 'Why Your Website Is Invisible to ChatGPT',
  },
]

const TREND = {
  search: [42, 45, 43, 48, 50, 52, 54, 53, 57, 59, 62, 64],
  ai: [18, 20, 22, 25, 28, 30, 34, 37, 39, 43, 47, 52],
}

/** A shorter period shows a fraction of the same synthetic totals. */
const PERIOD_FACTOR: Record<number, number> = { 7: 0.08, 30: 0.34, 90: 1 }

const compact = (n: number) =>
  n >= 1e6
    ? `${(n / 1e6).toFixed(1)}M`
    : n >= 1e3
      ? `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1)}K`
      : Math.round(n).toLocaleString()

const gapFor = (q: Query) => {
  if (q.citations.length <= 1)
    return {
      label: 'High',
      className: 'sip-gap-high',
      action: 'Build citation evidence',
    }
  if (q.citations.length <= 2 || q.position > 8)
    return {
      label: 'Medium',
      className: 'sip-gap-medium',
      action: 'Close search and citation gap',
    }
  return { label: 'Low', className: 'sip-gap-low', action: 'Defend and expand' }
}

export default function SEOIntelligencePlatformContent() {
  const [period, setPeriod] = useState(90)
  const [intent, setIntent] = useState('All')
  const [search, setSearch] = useState('')
  const [platform, setPlatform] = useState('all')
  const [selected, setSelected] = useState('visibility-audit')
  const [briefOpen, setBriefOpen] = useState(false)
  const [copyLabel, setCopyLabel] = useState('Copy mock summary')

  // Picking a platform surfaces the queries it does NOT cite -- the gap view.
  const items = useMemo(
    () =>
      QUERIES.filter(
        q =>
          (intent === 'All' || q.intent === intent) &&
          q.query.includes(search.toLowerCase()) &&
          (platform === 'all' || !q.citations.includes(platform))
      ),
    [intent, search, platform]
  )

  const focusId =
    items.length && !items.some(q => q.id === selected) ? items[0].id : selected
  const focus = QUERIES.find(q => q.id === focusId) ?? QUERIES[0]
  const factor = PERIOD_FACTOR[period]

  const kpis = useMemo(() => {
    const impressions = items.reduce((s, q) => s + q.impressions, 0)
    const clicks = items.reduce((s, q) => s + q.clicks, 0)
    const citationCount = items.reduce((s, q) => s + q.citations.length, 0)
    const avg = impressions
      ? items.reduce((s, q) => s + q.position * q.impressions, 0) / impressions
      : 0
    return {
      queries: items.length,
      impressions: compact(impressions * factor),
      clicks: compact(clicks * factor),
      coverage: items.length
        ? `${((citationCount / (items.length * 5)) * 100).toFixed(1)}%`
        : '0%',
      position: items.length ? avg.toFixed(1) : '—',
      gaps: items.filter(q => gapFor(q).label !== 'Low').length,
    }
  }, [items, factor])

  // ── chart geometry, unchanged from the source ────────────────────────────
  const chart = useMemo(() => {
    const count = period === 7 ? 7 : period === 30 ? 10 : 12
    const a = TREND.search.slice(-count)
    const b = TREND.ai.slice(-count)
    const x = (i: number) => 46 + (i / (count - 1)) * 646
    const y = (v: number) => 222 - (v / 100) * 180
    const path = (arr: number[]) =>
      arr
        .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
        .join(' ')
    return {
      a,
      b,
      x,
      y,
      searchLine: path(a),
      aiLine: path(b),
      searchArea: `${path(a)} L692,222 L46,222 Z`,
    }
  }, [period])

  const engines = useMemo(
    () =>
      PLATFORMS.map(p => {
        const hits = QUERIES.filter(q => q.citations.includes(p.id)).length
        return { ...p, rate: (hits / QUERIES.length) * 100 }
      }),
    []
  )

  const missing = PLATFORMS.filter(p => !focus.citations.includes(p.id)).map(
    p => p.name
  )
  const gap = gapFor(focus)

  const copyBrief = async () => {
    const text = `MOCK SEO BRIEF: ${focus.query}. ${focus.brief}. Current synthetic position ${focus.position.toFixed(1)}; cited by ${focus.citations.length}/5 engines. ${gap.action}.`
    try {
      await navigator.clipboard.writeText(text)
      setCopyLabel('Mock summary copied')
    } catch (error) {
      console.error('Clipboard write failed', error)
      setCopyLabel('Copy unavailable')
    }
    setTimeout(() => setCopyLabel('Copy mock summary'), 1800)
  }

  return (
    <div className='sip-cs'>
      <section className='sip-hero' aria-labelledby='page-title'>
        <div className='sip-wrap sip-hero-grid'>
          <div className='sip-hero-copy'>
            <div className='sip-badges'>
              <span className='sip-badge'>Marketing Tech</span>
              <span className='sip-badge'>2025</span>
              <span className='sip-badge'>Demo</span>
            </div>
            <h1 className='sip-display' id='page-title'>
              SEO Intelligence <span className='sip-accent'>Platform</span>
            </h1>
            <p className='sip-hero-lead'>
              Built with Google Search Console MCP to create an autonomous SEO
              optimization system that monitors rankings, identifies
              search-to-citation gaps, and turns verified performance data into
              evidence-linked content briefs.
            </p>
            <div className='sip-hero-actions'>
              <a className='sip-btn sip-btn-yellow' href='#dashboard-demo'>
                Explore mock dashboard <span aria-hidden='true'>↓</span>
              </a>
            </div>
            <div className='sip-hero-stack'>
              <p className='sip-label'>Technologies:</p>
              <div className='sip-tech-list'>
                <span className='sip-tech'>TypeScript</span>
                <span className='sip-tech'>MCP Protocol</span>
                <span className='sip-tech'>Google Search Console API</span>
                <span className='sip-tech'>Claude AI</span>
                <span className='sip-tech'>Data Analytics</span>
              </div>
            </div>
          </div>
          <figure className='sip-signal-figure'>
            <div className='sip-signal-index'>
              <span>Dual-channel signal map · 01</span>
              <span>Mock query set</span>
            </div>
            <div className='sip-signal-body'>
              <header className='sip-signal-head'>
                <div>
                  <p className='sip-eyebrow'>Illustrative visibility data</p>
                  <h2>
                    Ranked in search.
                    <br />
                    Missing in answers.
                  </h2>
                </div>
                <span className='sip-live-chip'>Base scan</span>
              </header>
              <div className='sip-signal-column-labels' aria-hidden='true'>
                <span>Google</span>
                <span>Query route</span>
                <span>GPT · PX · CL · GEM · AIO</span>
              </div>
              <div
                className='sip-signal-rows'
                aria-label='Five mock queries compared across Google rank and AI citations'
              >
                <div
                  className='sip-signal-row'
                  style={{ '--delay': '.05s' } as CSSProperties}
                >
                  <span className='sip-rank'>
                    4.2<small>POS</small>
                  </span>
                  <span className='sip-query-name'>
                    ai visibility audit<small>Commercial</small>
                  </span>
                  <span className='sip-engine-dots'>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GPT: cited`}
                    >
                      GPT
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`PX: cited`}
                    >
                      PX
                    </span>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`CL: not cited`}
                    >
                      CL
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GEM: cited`}
                    >
                      GEM
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`AIO: cited`}
                    >
                      AIO
                    </span>
                  </span>
                </div>
                <div
                  className='sip-signal-row'
                  style={{ '--delay': '.14s' } as CSSProperties}
                >
                  <span className='sip-rank'>
                    6.7<small>POS</small>
                  </span>
                  <span className='sip-query-name'>
                    geo agency philippines<small>Commercial</small>
                  </span>
                  <span className='sip-engine-dots'>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`GPT: not cited`}
                    >
                      GPT
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`PX: cited`}
                    >
                      PX
                    </span>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`CL: not cited`}
                    >
                      CL
                    </span>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`GEM: not cited`}
                    >
                      GEM
                    </span>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`AIO: not cited`}
                    >
                      AIO
                    </span>
                  </span>
                </div>
                <div
                  className='sip-signal-row'
                  style={{ '--delay': '.23s' } as CSSProperties}
                >
                  <span className='sip-rank'>
                    3.1<small>POS</small>
                  </span>
                  <span className='sip-query-name'>
                    what is llms.txt<small>Informational</small>
                  </span>
                  <span className='sip-engine-dots'>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GPT: cited`}
                    >
                      GPT
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`PX: cited`}
                    >
                      PX
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`CL: cited`}
                    >
                      CL
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GEM: cited`}
                    >
                      GEM
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`AIO: cited`}
                    >
                      AIO
                    </span>
                  </span>
                </div>
                <div
                  className='sip-signal-row'
                  style={{ '--delay': '.32s' } as CSSProperties}
                >
                  <span className='sip-rank'>
                    5.8<small>POS</small>
                  </span>
                  <span className='sip-query-name'>
                    ai product development agency<small>Commercial</small>
                  </span>
                  <span className='sip-engine-dots'>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GPT: cited`}
                    >
                      GPT
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`PX: cited`}
                    >
                      PX
                    </span>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`CL: not cited`}
                    >
                      CL
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GEM: cited`}
                    >
                      GEM
                    </span>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`AIO: not cited`}
                    >
                      AIO
                    </span>
                  </span>
                </div>
                <div
                  className='sip-signal-row'
                  style={{ '--delay': '.41s' } as CSSProperties}
                >
                  <span className='sip-rank'>
                    7.1<small>POS</small>
                  </span>
                  <span className='sip-query-name'>
                    ai sales qualification<small>Comparison</small>
                  </span>
                  <span className='sip-engine-dots'>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GPT: cited`}
                    >
                      GPT
                    </span>
                    <span
                      className='sip-engine-dot sip-miss'
                      aria-label={`PX: not cited`}
                    >
                      PX
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`CL: cited`}
                    >
                      CL
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`GEM: cited`}
                    >
                      GEM
                    </span>
                    <span
                      className='sip-engine-dot sip-hit'
                      aria-label={`AIO: cited`}
                    >
                      AIO
                    </span>
                  </span>
                </div>
              </div>
              <div className='sip-signal-kpis'>
                <div className='sip-signal-kpi'>
                  <span>Search index</span>
                  <strong>64</strong>
                </div>
                <div className='sip-signal-kpi'>
                  <span>AI citation index</span>
                  <strong>52</strong>
                </div>
                <div className='sip-signal-kpi'>
                  <span>Visibility gap</span>
                  <strong>12 pts</strong>
                </div>
              </div>
              <figcaption className='sip-signal-caption'>
                <strong>Demo · Synthetic data</strong>
                <span>Cyan = cited · Pink = citation gap</span>
              </figcaption>
            </div>
          </figure>
        </div>
      </section>

      <section
        className='sip-dashboard-section'
        id='dashboard-demo'
        aria-labelledby='dashboard-title'
      >
        <div className='sip-wrap'>
          <div className='sip-section-intro'>
            <div>
              <p className='sip-eyebrow'>
                Interactive product concept · Mock data
              </p>
              <h2 className='sip-display' id='dashboard-title'>
                One query.
                <br />
                Two visibility systems.
              </h2>
            </div>
            <p>
              A redesigned demonstration of the platform pattern: filter a
              synthetic query set, see where search ranking and AI citation
              disagree, and turn the widest gap into a content brief. Every
              number is invented.
            </p>
          </div>

          <div className='sip-dashboard'>
            <div className='sip-dash-bar'>
              <div className='sip-dash-title'>
                <span className='sip-live-chip' aria-hidden='true' />
                <span>SEO + GEO intelligence desk</span>
              </div>
              <div className='sip-dash-title sip-dash-warning'>
                Synthetic data · Not a live Search Console connection
              </div>
            </div>

            <div className='sip-controls'>
              <div className='sip-control sip-period'>
                <p className='sip-label'>01 · Period</p>
                <div className='sip-segments'>
                  {[7, 30, 90].map(d => (
                    <button
                      key={d}
                      className='sip-segment'
                      type='button'
                      aria-pressed={period === d}
                      onClick={() => setPeriod(d)}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <div className='sip-control'>
                <p className='sip-label'>02 · Search intent</p>
                <div className='sip-segments'>
                  {[
                    ['All', 'All'],
                    ['Commercial', 'Commercial'],
                    ['Informational', 'Info'],
                    ['Comparison', 'Compare'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      className='sip-segment'
                      type='button'
                      aria-pressed={intent === value}
                      onClick={() => setIntent(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className='sip-control'>
                <label className='sip-label' htmlFor='query-search'>
                  03 · Filter queries
                </label>
                <div className='sip-search-box'>
                  <input
                    id='query-search'
                    type='search'
                    placeholder='Search mock queries'
                    value={search}
                    onChange={e => setSearch(e.target.value.trim())}
                  />
                </div>
              </div>
            </div>

            <div className='sip-kpi-grid' aria-label='Mock visibility metrics'>
              <div className='sip-kpi'>
                <p className='sip-label'>Queries</p>
                <strong>{kpis.queries}</strong>
                <small>Visible mock set</small>
              </div>
              <div className='sip-kpi'>
                <p className='sip-label'>Impressions</p>
                <strong>{kpis.impressions}</strong>
                <small>Selected period</small>
              </div>
              <div className='sip-kpi'>
                <p className='sip-label'>Clicks</p>
                <strong>{kpis.clicks}</strong>
                <small>Synthetic GSC</small>
              </div>
              <div className='sip-kpi'>
                <p className='sip-label'>AI coverage</p>
                <strong>{kpis.coverage}</strong>
                <small>Across 5 engines</small>
              </div>
              <div className='sip-kpi'>
                <p className='sip-label'>Avg position</p>
                <strong>{kpis.position}</strong>
                <small>Impression-weighted</small>
              </div>
              <div className='sip-kpi'>
                <p className='sip-label'>Open gaps</p>
                <strong>{kpis.gaps}</strong>
                <small>Needs attention</small>
              </div>
            </div>

            <div className='sip-analysis-grid'>
              <section
                className='sip-trend-panel'
                aria-labelledby='trend-title'
              >
                <div className='sip-panel-head'>
                  <div>
                    <p className='sip-label'>Visibility trend</p>
                    <h3 id='trend-title'>Search index vs AI citations</h3>
                  </div>
                  <div className='sip-panel-meta'>{period}-day mock view</div>
                </div>
                <div className='sip-chart-wrap'>
                  <svg
                    className='sip-trend-chart'
                    viewBox='0 0 720 290'
                    role='img'
                    aria-label={`${period}-day synthetic trend. Search visibility ends at index ${chart.a[chart.a.length - 1]} and AI citations end at index ${chart.b[chart.b.length - 1]}.`}
                  >
                    <defs>
                      <linearGradient
                        id='sip-search-fill'
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='1'
                      >
                        <stop offset='0' stopColor='#5cb8e6' stopOpacity='.3' />
                        <stop offset='1' stopColor='#5cb8e6' stopOpacity='0' />
                      </linearGradient>
                    </defs>
                    <g aria-hidden='true'>
                      {[42, 102, 162, 222].map(yy => (
                        <line
                          key={yy}
                          className='sip-chart-gridline'
                          x1='46'
                          y1={yy}
                          x2='692'
                          y2={yy}
                        />
                      ))}
                      {[
                        ['100', 45],
                        ['66', 105],
                        ['33', 165],
                        ['0', 225],
                      ].map(([label, yy]) => (
                        <text
                          key={label}
                          className='sip-chart-axis'
                          x='8'
                          y={yy as number}
                        >
                          {label}
                        </text>
                      ))}
                    </g>
                    <path
                      className='sip-search-area'
                      d={chart.searchArea}
                      fill='url(#sip-search-fill)'
                    />
                    <path className='sip-search-line' d={chart.searchLine} />
                    <path className='sip-ai-line' d={chart.aiLine} />
                    <g aria-hidden='true'>
                      {chart.a.map((v, i) => (
                        <g key={i}>
                          <circle
                            className='sip-chart-dot'
                            cx={chart.x(i)}
                            cy={chart.y(v)}
                            r='2.8'
                            stroke='#5cb8e6'
                          />
                          <circle
                            className='sip-chart-dot'
                            cx={chart.x(i)}
                            cy={chart.y(chart.b[i])}
                            r='2.8'
                            stroke='#f90b8a'
                          />
                        </g>
                      ))}
                    </g>
                  </svg>
                </div>
                <div className='sip-chart-legend'>
                  <span className='sip-legend-key'>Search visibility</span>
                  <span className='sip-legend-key'>AI citations</span>
                </div>
              </section>

              <section
                className='sip-engine-panel'
                aria-labelledby='engine-title'
              >
                <div className='sip-panel-head'>
                  <div>
                    <p className='sip-label'>Citation coverage</p>
                    <h3 id='engine-title'>Inspect an engine gap</h3>
                  </div>
                </div>
                <div className='sip-engine-list'>
                  <button
                    className='sip-engine-button'
                    type='button'
                    aria-pressed={platform === 'all'}
                    onClick={() => setPlatform('all')}
                  >
                    <span className='sip-engine-name'>All engines</span>
                    <span className='sip-engine-track'>
                      <span
                        className='sip-engine-fill'
                        style={{ '--coverage': '100%' } as CSSProperties}
                      />
                    </span>
                    <strong>VIEW</strong>
                  </button>
                  {engines.map(p => (
                    <button
                      key={p.id}
                      className='sip-engine-button'
                      type='button'
                      aria-pressed={platform === p.id}
                      onClick={() => setPlatform(p.id)}
                    >
                      <span className='sip-engine-name'>{p.name}</span>
                      <span className='sip-engine-track'>
                        <span
                          className='sip-engine-fill'
                          style={
                            { '--coverage': `${p.rate}%` } as CSSProperties
                          }
                        />
                      </span>
                      <strong>{Math.round(p.rate)}%</strong>
                    </button>
                  ))}
                </div>
                <p className='sip-engine-note'>
                  Selecting an engine filters the ledger to mock queries it does
                  not cite.
                </p>
              </section>
            </div>

            <section className='sip-ledger' aria-labelledby='ledger-title'>
              <div className='sip-panel-head'>
                <div>
                  <p className='sip-label'>Unified query ledger</p>
                  <h3 id='ledger-title'>Search demand × AI citations</h3>
                </div>
                <div className='sip-panel-meta'>
                  {items.length} {items.length === 1 ? 'query' : 'queries'}
                </div>
              </div>
              <div className='sip-table-scroll'>
                <table>
                  <caption>
                    Mock queries only. Select a query to update the opportunity
                    brief.
                  </caption>
                  <thead>
                    <tr>
                      {[
                        'Query',
                        'Intent',
                        'Impressions',
                        'Position',
                        'CTR',
                        'Citations',
                        'Gap',
                      ].map(h => (
                        <th key={h} scope='col'>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.length ? (
                      items.map(q => {
                        const g = gapFor(q)
                        const ctr = (q.clicks / q.impressions) * 100
                        return (
                          <tr key={q.id} data-selected={q.id === focusId}>
                            <td>
                              <button
                                className='sip-query-select'
                                type='button'
                                onClick={() => setSelected(q.id)}
                              >
                                {q.query}
                              </button>
                            </td>
                            <td>
                              <span className='sip-intent-tag'>{q.intent}</span>
                            </td>
                            <td>{compact(q.impressions * factor)}</td>
                            <td>{q.position.toFixed(1)}</td>
                            <td>{ctr.toFixed(1)}%</td>
                            <td>{q.citations.length}/5</td>
                            <td>
                              <span className={`sip-gap-tag ${g.className}`}>
                                {g.label}
                              </span>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          No mock queries match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className='sip-brief-panel' aria-labelledby='brief-title'>
              <p className='sip-label'>AI content brief · Mock output</p>
              <h3 className='sip-brief-query' id='brief-title'>
                {focus.query}
              </h3>
              <p className='sip-brief-route'>{focus.page}</p>
              <div className='sip-brief-grid'>
                <div className='sip-brief-row'>
                  <span>Priority</span>
                  <span>{gap.action}</span>
                </div>
                <div className='sip-brief-row'>
                  <span>Search</span>
                  <span>
                    Position {focus.position.toFixed(1)} ·{' '}
                    {compact(focus.impressions * factor)} impressions
                  </span>
                </div>
                <div className='sip-brief-row'>
                  <span>AI gap</span>
                  <span>
                    {missing.length
                      ? `Missing from ${missing.join(', ')}`
                      : 'Cited by all five platforms'}
                  </span>
                </div>
                <div className='sip-brief-row'>
                  <span>Brief title</span>
                  <span>{focus.brief}</span>
                </div>
              </div>
              <div className='sip-brief-actions'>
                <button
                  className='sip-brief-button'
                  type='button'
                  aria-expanded={briefOpen}
                  onClick={() => setBriefOpen(!briefOpen)}
                >
                  {briefOpen ? 'Hide mock brief' : 'Build mock brief'}
                </button>
                <button
                  className='sip-copy-button'
                  type='button'
                  onClick={copyBrief}
                >
                  {copyLabel}
                </button>
              </div>
              <div className='sip-brief-extra' hidden={!briefOpen}>
                <p>
                  <strong>Working title.</strong> {focus.brief}
                </p>
                <p>
                  <strong>Target page.</strong> {focus.page}
                </p>
                <p>
                  <strong>Why now.</strong> {gap.action.toLowerCase()}:{' '}
                  {missing.length
                    ? `no citation on ${missing.join(', ')}`
                    : 'already cited across all five engines'}
                  , mock position {focus.position.toFixed(1)}.
                </p>
              </div>
            </aside>

            <div className='sip-dash-foot'>
              <span>Selected: {focus.query}</span>
              <span>
                Demo · Synthetic data · Not a live Search Console connection
              </span>
            </div>
            <p className='sip-sr-only' aria-live='polite'>
              {`${focus.query} selected. Mock position ${focus.position.toFixed(1)}, ${focus.citations.length} of 5 AI platforms citing, ${gap.label.toLowerCase()} gap.`}
            </p>
          </div>
        </div>
      </section>

      <section className='sip-problem-solution' aria-labelledby='problem-title'>
        <div className='sip-wrap sip-split-board'>
          <article className='sip-split-side'>
            <p className='sip-eyebrow'>The Problem</p>
            <h2 className='sip-display' id='problem-title'>
              Search changed. The old dashboard did not.
            </h2>
            <p>
              Traditional SEO tools were built around search rankings. AI answer
              engines created a second, sampled visibility surface: whether a
              brand appears and is cited in generated answers.
            </p>
            <p>
              The result? A team can perform well in Google and still have weak
              coverage across monitored AI prompts. The two systems need to be
              measured together.
            </p>
          </article>
          <article className='sip-split-side'>
            <p className='sip-eyebrow'>The Solution</p>
            <h2 className='sip-display'>One interface for SEO and GEO.</h2>
            <p>
              The SEO Intelligence Platform brings traditional SEO and sampled
              AI search visibility into one interface. It tracks keyword
              performance alongside citation rates across a defined prompt set,
              giving teams one view of both systems.
            </p>
            <p>
              An AI-powered content brief generator creates strategies optimized
              for both Google and generative engines, closing the gap between
              SEO and GEO (Generative Engine Optimization).
            </p>
          </article>
        </div>
      </section>

      <section className='sip-capabilities' aria-labelledby='cap-title'>
        <div className='sip-wrap'>
          <div className='sip-cap-head'>
            <div>
              <p className='sip-eyebrow'>What the system watches</p>
              <h2 className='sip-display' id='cap-title'>
                Six instruments. One visibility picture.
              </h2>
            </div>
            <p>
              Each instrument answers a different operational question: where
              demand exists, where the brand appears, where it is absent, and
              what the team should build next.
            </p>
          </div>
          <div className='sip-cap-grid'>
            <article className='sip-cap'>
              <span className='sip-cap-code'>Citation monitor</span>
              <h3>AI Citation Tracking</h3>
              <p>
                Monitor when and where your brand appears in ChatGPT,
                Perplexity, Claude, Google AI Overviews, and Gemini responses.
              </p>
            </article>
            <article className='sip-cap'>
              <span className='sip-cap-code'>Search console</span>
              <h3>Unified SEO Dashboard</h3>
              <p>
                Keywords, rankings, impressions, clicks, and CTR from Google
                Search Console in one view.
              </p>
            </article>
            <article className='sip-cap'>
              <span className='sip-cap-code'>Strategy output</span>
              <h3>AI Content Brief Generator</h3>
              <p>
                GPT-4o-mini powered briefs with SEO targets, GEO
                recommendations, and competitor gap analysis.
              </p>
            </article>
            <article className='sip-cap'>
              <span className='sip-cap-code'>Opportunity queue</span>
              <h3>Content Gap Analysis</h3>
              <p>
                Identifies high-impression keywords without dedicated landing
                pages to prioritize content creation.
              </p>
            </article>
            <article className='sip-cap'>
              <span className='sip-cap-code'>Cross-engine view</span>
              <h3>5-Platform Monitoring</h3>
              <p>
                Cross-platform citation rates with trend analysis showing
                visibility changes over time.
              </p>
            </article>
            <article className='sip-cap'>
              <span className='sip-cap-code'>Site health</span>
              <h3>Technical Health Audit</h3>
              <p>
                Core Web Vitals, Lighthouse scores, crawl statistics, and
                actionable recommendations.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='sip-results' aria-labelledby='results-title'>
        <h2 className='sip-sr-only' id='results-title'>
          Published platform scope
        </h2>
        <div className='sip-wrap sip-result-grid'>
          <article className='sip-result'>
            <p className='sip-label'>AI platforms tracked simultaneously</p>
            <strong>5</strong>
          </article>
          <article className='sip-result'>
            <p className='sip-label'>
              Keywords monitored with daily GSC updates
            </p>
            <strong>40+</strong>
          </article>
          <article className='sip-result'>
            <p className='sip-label'>Historical trend analysis depth</p>
            <strong>90d</strong>
          </article>
        </div>
      </section>

      <section
        className='sip-architecture'
        aria-labelledby='architecture-title'
      >
        <div className='sip-wrap sip-architecture-board'>
          <div className='sip-architecture-copy'>
            <p className='sip-eyebrow'>Technical Architecture</p>
            <h2 className='sip-display' id='architecture-title'>
              The intelligence pipeline.
            </h2>
            <p>
              The published case study describes this stack for scheduled
              search-data collection, AI-assisted brief generation, interactive
              analysis, and Row Level Security.
            </p>
          </div>
          <div className='sip-stack-list'>
            <div className='sip-stack-row'>
              <strong>Next.js 15</strong>
              <span>
                Server-rendered dashboard with client-side interactivity
              </span>
            </div>
            <div className='sip-stack-row'>
              <strong>Supabase + PostgreSQL</strong>
              <span>Data storage with Row Level Security</span>
            </div>
            <div className='sip-stack-row'>
              <strong>OpenAI GPT-4o-mini</strong>
              <span>AI-powered content brief generation</span>
            </div>
            <div className='sip-stack-row'>
              <strong>Recharts</strong>
              <span>Interactive data visualization and trend analysis</span>
            </div>
            <div className='sip-stack-row'>
              <strong>Google Search Console MCP</strong>
              <span>Scheduled keyword-performance data collection</span>
            </div>
            <div className='sip-stack-row'>
              <strong>TypeScript</strong>
              <span>End-to-end type safety across all layers</span>
            </div>
          </div>
        </div>
      </section>

      <section className='sip-process' aria-labelledby='process-title'>
        <div className='sip-wrap'>
          <div className='sip-process-head'>
            <p className='sip-eyebrow'>How It Works</p>
            <h2 className='sip-display' id='process-title'>
              Collect. Unify. Find the gap. Build the answer.
            </h2>
          </div>
          <div className='sip-process-grid'>
            <article className='sip-process-step'>
              <span className='sip-step-number'>01 / INPUT</span>
              <h3>Data Collection</h3>
              <p>
                Google Search Console MCP pulls keyword rankings, impressions,
                and clicks daily. AI visibility monitors run queries across 5
                platforms to check citation status.
              </p>
            </article>
            <article className='sip-process-step'>
              <span className='sip-step-number'>02 / MERGE</span>
              <h3>Unified Analysis</h3>
              <p>
                Traditional SEO metrics merge with AI citation data in a single
                dashboard. Health scores combine both signals for a complete
                visibility picture.
              </p>
            </article>
            <article className='sip-process-step'>
              <span className='sip-step-number'>03 / GAP</span>
              <h3>Gap Identification</h3>
              <p>
                The system identifies high-impression keywords without pages,
                and queries where competitors get AI citations but you do not.
              </p>
            </article>
            <article className='sip-process-step'>
              <span className='sip-step-number'>04 / OUTPUT</span>
              <h3>AI-Powered Strategy</h3>
              <p>
                Content brief generator creates guides optimized for Google
                rankings and AI engine citations, including GEO-specific
                recommendations.
              </p>
            </article>
          </div>
        </div>
      </section>
    </div>
  )
}
